<template>
  <div class="row">
    <notifications></notifications>

    <!-- Hotelkontingente -->
    <div class="col-xl-12">
      <container-headline class="col-xl-12" :headline="$t('global.hotelquotas')" :col="3">
        <div class="row d-flex justify-content-between">
          <div class="ml-4">
            <button v-if="editable && zimmerRecht('create')" class="btn btn-success" @click="kontingentOeffnen(null)">
              <font-awesome-icon icon="fa-regular fa-plus" class="mr-2" />
              <span>{{ $t("global.new") }}</span>
            </button>
          </div>
          <div class="mr-4">
            <button v-if="editable && zimmerRecht('delete')" class="btn btn-danger" @click="kontingenteLoeschenFragen">
              <font-awesome-icon icon="fa-duotone fa-trash" class="mr-2" />{{ $t("global.delete") }}
            </button>
          </div>
        </div>
      </container-headline>
      <div class="row col-xl-12">
        <div class="col-xl-12 block belegliste br-t-l-0">
          <b-table
            small
            responsive
            show-empty
            tbody-tr-class="item"
            :items="hotelkontingente"
            :fields="kontingentFields"
            :busy="isBusy"
            @row-dblclicked="kontingentOeffnen"
          >
            <template v-slot:cell(selected)="row">
              <b-form-checkbox v-model="gewaehlteKontingente" :value="row.item.id"></b-form-checkbox>
            </template>
            <template v-slot:cell(modul)="row">
              {{ row.item.veranstaltung ? row.item.veranstaltung.bezeichnung : $t("global.allmodules") }}
            </template>
            <template v-slot:cell(belegung)="row">
              <span :class="{ 'text-danger font-weight-bold': ueberbucht(row.item) }">
                {{ row.item.maxBelegungProNacht }}<span v-if="row.item.kontingent != null"> / {{ row.item.kontingent }}</span>
              </span>
            </template>
            <template #cell(edit)="row">
              <div role="button" @click="kontingentOeffnen(row.item)">
                <font-awesome-icon icon="fa-regular fa-pencil" size="sm" />
              </div>
            </template>
            <template #table-busy>
              <div class="text-center text-primary my-2"><b-spinner class="align-middle"></b-spinner></div>
            </template>
            <template #empty>
              <div class="text-center my-2" :class="ladeFehler ? 'text-danger' : 'text-primary'">
                <strong>{{ ladeFehler ? $t("global.errorwhileloading") : $t("global.nodataavailable") }}</strong>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </div>

    <!-- Zimmerreservationen -->
    <div class="col-xl-12 mt-3">
      <container-headline class="col-xl-12" :headline="$t('global.roomreservations')" :col="3">
        <div class="row d-flex justify-content-between">
          <div class="ml-4 d-flex align-items-center">
            <button v-if="editable && zimmerRecht('create')" class="btn btn-success" @click="reservationOeffnen(null)">
              <font-awesome-icon icon="fa-regular fa-plus" class="mr-2" />
              <span>{{ $t("global.new") }}</span>
            </button>
            <button v-if="editable && zimmerRecht('create')" class="btn btn-primary ml-2" @click="$refs.zuteilenModal.oeffnen()">
              <font-awesome-icon icon="fa-duotone fa-users-gear" class="mr-2" />
              <span>{{ $t("global.assignrooms") }}</span>
            </button>
            <button v-if="editable && zimmerRecht('create')" class="btn btn-primary ml-2" @click="ausZusatzfragenFragen">
              <font-awesome-icon icon="fa-duotone fa-user-check" class="mr-2" />
              <span>{{ $t("global.createfromroomrequests") }}</span>
            </button>
            <button v-if="editable && zimmerRecht('update')" class="btn btn-primary ml-2" @click="hotelZuweisenFragen">
              <font-awesome-icon icon="fa-duotone fa-building" class="mr-2" />
              <span>{{ $t("global.assignhotel") }}</span>
            </button>
            <b-form-checkbox v-model="nurOffene" class="ml-3" switch>
              {{ $t("global.onlywithouthotel") }} ({{ anzahlOffene }})
            </b-form-checkbox>
          </div>
          <div class="mr-4">
            <button v-if="editable && zimmerRecht('delete')" class="btn btn-danger" @click="reservationenLoeschenFragen">
              <font-awesome-icon icon="fa-duotone fa-trash" class="mr-2" />{{ $t("global.delete") }}
            </button>
          </div>
        </div>
      </container-headline>
      <div class="row col-xl-12">
        <div class="col-xl-12 block belegliste br-t-l-0">
          <b-table
            small
            responsive
            show-empty
            sort-icon-left
            tbody-tr-class="item"
            :items="angezeigteReservationen"
            :fields="reservationFields"
            :busy="isBusy"
            @row-dblclicked="(item) => reservationOeffnen(item.id)"
          >
            <template v-slot:head(selected)>
              <b-form-checkbox :checked="alleReservationenGewaehlt" @change="alleReservationenWaehlen"></b-form-checkbox>
            </template>
            <template v-slot:cell(selected)="row">
              <b-form-checkbox v-model="gewaehlteReservationen" :value="row.item.id"></b-form-checkbox>
            </template>
            <template v-slot:cell(gebaeude)="row">
              <span v-if="row.item.gebaeude">{{ row.item.gebaeude.bezeichnung }}</span>
              <b-badge v-else variant="warning">{{ $t("global.hotelopen") }}</b-badge>
            </template>
            <template v-slot:cell(voranreise)="row">
              <font-awesome-icon v-if="row.item.voranreise" icon="fa-solid fa-check" />
            </template>
            <template #cell(edit)="row">
              <div role="button" @click="reservationOeffnen(row.item.id)">
                <font-awesome-icon icon="fa-regular fa-pencil" size="sm" />
              </div>
            </template>
            <template #table-busy>
              <div class="text-center text-primary my-2"><b-spinner class="align-middle"></b-spinner></div>
            </template>
            <template #empty>
              <div class="text-center my-2" :class="ladeFehler ? 'text-danger' : 'text-primary'">
                <strong>{{ ladeFehler ? $t("global.errorwhileloading") : $t("global.nodataavailable") }}</strong>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </div>

    <!-- Belegung pro Nacht -->
    <div class="col-xl-12 mt-3">
      <container-headline class="col-xl-12" :headline="$t('global.occupancypernight')" :col="3"></container-headline>
      <div class="row col-xl-12">
        <div class="col-xl-12 block belegliste br-t-l-0">
          <zimmer-belegung
            v-if="geladen"
            :veranstaltung="veranstaltung"
            :lehrgang="lehrgang"
            :hotelkontingente="hotelkontingente"
            :reloadKey="belegungKey"
          />
        </div>
      </div>
    </div>

    <zimmer-zuteilen-modal
      ref="zuteilenModal"
      :id="'zimmer-zuteilen-modal-' + kontextId"
      :veranstaltung="veranstaltung"
      :lehrgang="lehrgang"
      :module="module"
      :hotelkontingente="hotelkontingente"
      :reservationstypen="reservationstypen"
      @saved="laden"
    />

    <hotelkontingent-modal
      ref="hotelkontingentModal"
      :id="'hotelkontingent-modal-' + kontextId"
      :veranstaltung="veranstaltung"
      :lehrgang="lehrgang"
      :module="module"
      :editable="editable && zimmerRecht('update')"
      @saved="laden"
    />

    <zimmerreservation-modal
      ref="zimmerreservationModal"
      :id="'zimmerreservation-modal-' + kontextId"
      :veranstaltung="veranstaltung"
      :lehrgang="lehrgang"
      :module="module"
      :hotelkontingente="hotelkontingente"
      :reservationstypen="reservationstypen"
      :editable="editable && zimmerRecht('update')"
      @saved="laden"
    />

    <!-- Massenmutation Hotel zuweisen -->
    <b-modal :id="'hotel-zuweisen-modal-' + kontextId" :title="$t('global.assignhotel')" no-close-on-backdrop>
      <div class="form-group">
        <v-select
          v-model="zuweisenKontingent"
          :getOptionLabel="kontingentLabel"
          :options="hotelkontingente"
          :reduce="(k) => k.id"
          :placeholder="$t('global.hotelopen')"
        >
          <span slot="no-options">{{ $t("global.taptosearch") }}</span>
        </v-select>
        <label>{{ $t("global.hotel") }}</label>
      </div>
      <template #modal-footer>
        <button class="btn btn-default mr-2" @click="$bvModal.hide('hotel-zuweisen-modal-' + kontextId)">
          {{ $t("global.cancel") }}
        </button>
        <button class="btn btn-primary" @click="hotelZuweisen">{{ $t("global.save") }}</button>
      </template>
    </b-modal>

    <loeschen-modal
      :id="'hotelkontingent-loeschen-modal-' + kontextId"
      :selectedIds="gewaehlteKontingente"
      :multiple="true"
      @confirmed="kontingenteLoeschen"
    />
    <loeschen-modal
      :id="'zimmerreservation-loeschen-modal-' + kontextId"
      :selectedIds="gewaehlteReservationen"
      :multiple="true"
      @confirmed="reservationenLoeschen"
    />
  </div>
</template>

<script>
import AppApi from "@/api/AppApi";
import { apiErrorToAlert } from "@/utils/Errorhandler";
import ContainerHeadline from "@/components/ContainerHeadline";
import LoeschenModal from "@/components/Modals/LoeschenModal";
import HotelkontingentModal from "@/components/Zimmer/HotelkontingentModal";
import ZimmerreservationModal from "@/components/Zimmer/ZimmerreservationModal";
import ZimmerZuteilenModal from "@/components/Zimmer/ZimmerZuteilenModal";
import ZimmerBelegung from "@/components/Zimmer/ZimmerBelegung";

/**
 * Reiter «Zimmereinteilung» auf Kurzveranstaltung (veranstaltung) bzw. Bildungsgang (lehrgang).
 * Oben die Hotelkontingente, unten die Zimmerreservationen inkl. «Hotel offen».
 */
export default {
  name: "Zimmereinteilung",
  components: {
    ContainerHeadline,
    LoeschenModal,
    HotelkontingentModal,
    ZimmerreservationModal,
    ZimmerZuteilenModal,
    ZimmerBelegung,
  },
  props: {
    veranstaltung: { type: String, required: false },
    lehrgang: { type: String, required: false },
    shown: { type: Boolean, required: false },
    editable: { type: Boolean, required: true },
  },
  data() {
    return {
      isBusy: false,
      ladeFehler: false,
      geladen: false,
      hotelkontingente: [],
      reservationen: [],
      module: [],
      reservationstypen: [],
      gewaehlteKontingente: [],
      gewaehlteReservationen: [],
      nurOffene: false,
      zuweisenKontingent: null,
      belegungKey: 0,
    };
  },
  computed: {
    kontextId() {
      return this.lehrgang || this.veranstaltung;
    },
    kontingentFields() {
      const fields = [
        { key: "selected", label: "" },
        { key: "gebaeude.bezeichnung", label: this.$t("global.hotel"), sortable: true },
      ];
      if (this.lehrgang) fields.push({ key: "modul", label: this.$t("global.module") });
      fields.push(
        { key: "belegung", label: this.$t("global.maxoccupancypernight") },
        { key: "anzahlReservationen", label: this.$t("global.roomreservations") },
        { key: "verrechnungsposition.bezeichnung", label: this.$t("global.clearingitempernight") },
        { key: "edit", label: "" }
      );
      return fields;
    },
    reservationFields() {
      const fields = [
        { key: "selected", label: "" },
        { key: "person.bezeichnung", label: this.$t("global.person"), sortable: true },
        { key: "reservationstyp.bezeichnung", label: this.$t("global.reservationtype"), sortable: true },
        { key: "gebaeude", label: this.$t("global.hotel"), sortable: true },
      ];
      if (this.lehrgang) fields.push({ key: "veranstaltung.bezeichnung", label: this.$t("global.module"), sortable: true });
      fields.push(
        { key: "anreise", label: this.$t("global.arrival") },
        { key: "abreise", label: this.$t("global.departure") },
        { key: "naechte", label: this.$t("global.nights") },
        { key: "voranreise", label: this.$t("global.arrivaltheeveningbefore") },
        { key: "edit", label: "" }
      );
      return fields;
    },
    angezeigteReservationen() {
      return this.nurOffene ? this.reservationen.filter((r) => !r.zimmer) : this.reservationen;
    },
    anzahlOffene() {
      return this.reservationen.filter((r) => !r.zimmer).length;
    },
    alleReservationenGewaehlt() {
      return (
        this.angezeigteReservationen.length > 0 &&
        this.angezeigteReservationen.every((r) => this.gewaehlteReservationen.includes(r.id))
      );
    },
  },
  watch: {
    shown(val) {
      if (val && !this.geladen) this.laden();
    },
    kontextId(val) {
      if (val && this.shown) this.laden();
    },
  },
  mounted() {
    if (this.shown && !this.geladen) this.laden();
  },
  methods: {
    laden() {
      if (!this.kontextId) return;
      const params = this.lehrgang ? { lehrgang: this.lehrgang } : { veranstaltung: this.veranstaltung };

      this.isBusy = true;
      AppApi.get("zimmer-reiter/", { params })
        .then((response) => {
          this.hotelkontingente = response.data.hotelkontingente || [];
          this.reservationen = response.data.reservationen || [];
          this.module = response.data.module || [];
          this.reservationstypen = response.data.reservationstypen || [];
          this.gewaehlteKontingente = [];
          this.gewaehlteReservationen = [];
          this.ladeFehler = false;
          this.geladen = true;
          this.belegungKey++;
        })
        .catch((e) => {
          this.$notify(apiErrorToAlert(e));
          this.ladeFehler = true;
        })
        .finally(() => {
          this.isBusy = false;
        });
    },
    ueberbucht(kontingent) {
      return kontingent.kontingent != null && kontingent.maxBelegungProNacht > kontingent.kontingent;
    },
    kontingentLabel(kontingent) {
      const modul = this.lehrgang
        ? ` (${kontingent.veranstaltung ? kontingent.veranstaltung.bezeichnung : this.$t("global.allmodules")})`
        : "";
      return `${kontingent.gebaeude?.bezeichnung || ""}${modul}`;
    },
    kontingentOeffnen(kontingent) {
      this.$refs.hotelkontingentModal.oeffnen(kontingent);
    },
    reservationOeffnen(reservationId) {
      this.$refs.zimmerreservationModal.oeffnen(reservationId);
    },
    alleReservationenWaehlen(checked) {
      const ids = this.angezeigteReservationen.map((r) => r.id);
      this.gewaehlteReservationen = checked
        ? [...new Set([...this.gewaehlteReservationen, ...ids])]
        : this.gewaehlteReservationen.filter((id) => !ids.includes(id));
    },
    keineAuswahl() {
      this.$notify({
        type: "warn",
        title: this.$t("global.actionfailed"),
        text: this.$t("global.noentryselected"),
      });
    },
    kontingenteLoeschenFragen() {
      if (this.gewaehlteKontingente.length === 0) return this.keineAuswahl();
      this.$bus.$emit("openLoeschenModal", "hotelkontingent-loeschen-modal-" + this.kontextId);
    },
    kontingenteLoeschen() {
      AppApi.delete("zimmer/", { params: { ids: this.gewaehlteKontingente.join(",") } })
        .then(() => this.laden())
        .catch((e) => this.$notify(apiErrorToAlert(e)));
    },
    reservationenLoeschenFragen() {
      if (this.gewaehlteReservationen.length === 0) return this.keineAuswahl();
      this.$bus.$emit("openLoeschenModal", "zimmerreservation-loeschen-modal-" + this.kontextId);
    },
    reservationenLoeschen() {
      AppApi.delete("zimmerreservation/", { params: { ids: this.gewaehlteReservationen.join(",") } })
        .then(() => this.laden())
        .catch((e) => this.$notify(apiErrorToAlert(e)));
    },
    ausZusatzfragenFragen() {
      this.$bvModal
        .msgBoxConfirm(this.$t("global.createfromroomrequestsconfirm"), {
          okTitle: this.$t("global.createfromroomrequests"),
          cancelTitle: this.$t("global.cancel"),
        })
        .then((ok) => {
          if (!ok) return;
          const params = this.lehrgang ? { lehrgang: this.lehrgang } : { veranstaltung: this.veranstaltung };
          AppApi.post("zimmer-zusatzfragen/", null, { params })
            .then((response) => {
              this.$notify({
                type: "success",
                title: this.$t("global.actionsuccessful"),
                text: `${response.data.erstellteReservationen} ${this.$t("global.roomreservations")}`,
              });
              this.laden();
            })
            .catch((e) => this.$notify(apiErrorToAlert(e)));
        });
    },
    hotelZuweisenFragen() {
      if (this.gewaehlteReservationen.length === 0) return this.keineAuswahl();
      this.zuweisenKontingent = null;
      this.$bvModal.show("hotel-zuweisen-modal-" + this.kontextId);
    },
    hotelZuweisen() {
      AppApi.put("zimmerreservation-hotel/", null, {
        params: { ids: this.gewaehlteReservationen.join(","), zimmer: this.zuweisenKontingent || "" },
      })
        .then(() => {
          this.$bvModal.hide("hotel-zuweisen-modal-" + this.kontextId);
          this.$notify({
            type: "success",
            title: this.$t("global.actionsuccessful"),
            text: this.$t("global.roomreservationsavedsuccessfully"),
          });
          this.laden();
        })
        .catch((e) => this.$notify(apiErrorToAlert(e)));
    },
  },
};
</script>
